import React, { createContext, useContext, useState, useEffect } from 'react';
import { db, auth, googleProvider } from '../firebase';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { signInWithPopup } from 'firebase/auth';

const GamerAuthContext = createContext();

export function useGamerAuth() {
    return useContext(GamerAuthContext);
}

export function GamerAuthProvider({ children }) {
    const [gamerUser, setGamerUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const augmentUserObj = (userObj) => {
        if (!userObj || !userObj.profileData) return userObj;
        const p = userObj.profileData;
        const rec = p.receivedReports || 0;
        const conf = p.confirmedReports || 0;
        let score = 100 - (rec * 4) - (conf * 33);
        if (conf >= 3) score = 0;
        if (score < 0) score = 0;
        return {
            ...userObj,
            calculatedTrustScore: score,
            isBanned: userObj.isBanned === true || score <= 0
        };
    };

    useEffect(() => {
        const storedUser = localStorage.getItem('gamer_user');
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                setGamerUser(augmentUserObj(parsedUser));
            } catch (e) {
                console.error("Failed to parse stored gamer user", e);
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        const uid = gamerUser?.uid;
        const gamerCode = gamerUser?.gamerCode;
        
        if (!uid && !gamerCode) return;
        
        let docRef;
        if (uid) {
            docRef = doc(db, 'users', uid);
        } else if (gamerCode) {
            docRef = doc(db, 'gameswap', 'data', 'gamers', gamerCode);
        }
        
        if (!docRef) return;

        const unsub = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                const updatedData = docSnap.data();
                setGamerUser(prev => {
                    if (!prev) return prev;
                    const newUserObj = { ...prev, ...updatedData };
                    // Preserve uid which might not be inside the document
                    if (uid && !newUserObj.uid) newUserObj.uid = uid;
                    const finalUserObj = augmentUserObj(newUserObj);
                    
                    if (JSON.stringify(prev) !== JSON.stringify(finalUserObj)) {
                        localStorage.setItem('gamer_user', JSON.stringify(finalUserObj));
                        return finalUserObj;
                    }
                    return prev;
                });
            }
        });

        return () => unsub();
    }, [gamerUser?.uid, gamerUser?.gamerCode]);


    const loginWithCode = async (gamerCode) => {
        const docRef = doc(db, 'gameswap', 'data', 'gamers', gamerCode);
        const docSnap = await getDoc(docRef);

        let userObj = {};
        if (docSnap.exists()) {
            userObj = docSnap.data();
        } else {
            // Auto-register a new gamer profile if it doesn't exist
            userObj = {
                gamerCode: gamerCode,
                unlockedGames: [],
                profileData: {
                    name: `Gamer_${gamerCode.substring(0, 4)}`,
                    joinedAt: new Date().toISOString(),
                    trustScore: 100,
                    receivedReports: 0,
                    filedReports: 0,
                    confirmedReports: 0
                }
            };
            await setDoc(docRef, userObj);
        }

        const finalUserObj = augmentUserObj(userObj);
        setGamerUser(finalUserObj);
        localStorage.setItem('gamer_user', JSON.stringify(finalUserObj));
        return finalUserObj;
    };

    const logoutGamer = () => {
        setGamerUser(null);
        localStorage.removeItem('gamer_user');
    };

    const unlockGames = async (gameIds) => {
        if (!gamerUser) return;
        const newUnlocked = [...new Set([...(gamerUser.unlockedGames || []), ...gameIds])];
        const docRef = doc(db, 'gameswap', 'data', 'gamers', gamerUser.gamerCode || gamerUser.uid);
        await setDoc(docRef, { unlockedGames: newUnlocked }, { merge: true });
        
        const updatedUser = { ...gamerUser, unlockedGames: newUnlocked };
        const finalUser = augmentUserObj(updatedUser);
        setGamerUser(finalUser);
        localStorage.setItem('gamer_user', JSON.stringify(finalUser));
    };

    const loginWithGoogle = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;
            
            const userRef = doc(db, 'users', user.uid);
            const docSnap = await getDoc(userRef);

            let userObj = {};
            if (docSnap.exists()) {
                userObj = docSnap.data();
                if (!userObj.profileData.referralCode) {
                    userObj.profileData.referralCode = 'GLPC' + user.uid.substring(0, 6).toUpperCase();
                    await setDoc(userRef, { profileData: { referralCode: userObj.profileData.referralCode } }, { merge: true });
                }
            } else {
                userObj = {
                    uid: user.uid,
                    displayName: user.displayName,
                    email: user.email,
                    photoURL: user.photoURL,
                    unlockedGames: [],
                    joinedAt: new Date().toISOString(),
                    profileData: {
                        name: user.displayName || 'AKSHAR OP',
                        username: `@${(user.displayName || 'gamer').replace(/\s+/g, '').toLowerCase()}`,
                        playerId: 'GLPC' + user.uid.substring(0, 6).toUpperCase(),
                        referralCode: 'GLPC' + user.uid.substring(0, 6).toUpperCase(),
                        state: 'MAHARASHTRA',
                        mobile: '',
                        upiId: '',
                        trustScore: 100,
                        gameUid2: '',
                        gameIgn2: '',
                        primaryUidSlot: 1,
                        eCoinsBalance: 0,
                        receivedReports: 0,
                        filedReports: 0,
                        confirmedReports: 0
                    }
                };
                await setDoc(userRef, userObj);
            }

            const finalUserObj = augmentUserObj(userObj);
            setGamerUser(finalUserObj);
            localStorage.setItem('gamer_user', JSON.stringify(finalUserObj));
            return finalUserObj;
        } catch (error) {
            console.error("Google login failed", error);
            throw error;
        }
    };

    const updateProfileData = async (newData) => {
        if (!gamerUser) return;
        if (gamerUser.isBanned) {
            console.warn("Write blocked: User is banned.");
            return;
        }
        
        const newProfileData = { ...gamerUser.profileData, ...newData };
        
        let docRef;
        if (gamerUser.uid) {
            docRef = doc(db, 'users', gamerUser.uid);
        } else if (gamerUser.gamerCode) {
            docRef = doc(db, 'gameswap', 'data', 'gamers', gamerUser.gamerCode);
        } else {
            return;
        }

        await setDoc(docRef, { profileData: newProfileData }, { merge: true });
        
        const updatedUser = { ...gamerUser, profileData: newProfileData };
        const finalUser = augmentUserObj(updatedUser);
        setGamerUser(finalUser);
        localStorage.setItem('gamer_user', JSON.stringify(finalUser));
    };

    return (
        <GamerAuthContext.Provider value={{ gamerUser, loading, loginWithCode, loginWithGoogle, logoutGamer, unlockGames, updateProfileData }}>
            {children}
        </GamerAuthContext.Provider>
    );
}
